import { useRegisterSW } from "virtual:pwa-register/react";

/**
 * Shows a small banner when a new service worker is waiting — i.e. a new
 * build has been deployed. With `registerType: 'autoUpdate'` +
 * `skipWaiting/clientsClaim` in vite.config, the SW activates immediately;
 * this lets the user reload to pick up the new bundle without losing state.
 *
 * Also re-checks every 5 minutes (not hourly — fast feedback during active
 * sessions matters more than network politeness).
 */
export function UpdatePrompt() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      // Poll every 5 minutes for a fresh SW while the app is open.
      setInterval(() => registration.update().catch(() => {}), 5 * 60 * 1000);
      // Also check the moment the user re-focuses the PWA (returning to
      // the app after backgrounding it). Critical for installed PWAs —
      // otherwise they can serve the cached-old bundle for a long time
      // if the user doesn't scroll or click, since polling only runs
      // while the tab is foregrounded.
      const checkNow = () => registration.update().catch(() => {});
      window.addEventListener("focus", checkNow);
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") checkNow();
      });
    },
  });

  if (!needRefresh) return null;

  return (
    <div className="pwa-update-toast">
      <span>ClearBooks has been updated.</span>
      <button
        type="button"
        className="btn btn-line"
        style={{ padding: "6px 14px", fontSize: 13 }}
        onClick={() => updateServiceWorker(true)}
      >
        Reload
      </button>
      <button
        type="button"
        className="btn btn-ghost"
        style={{ padding: "6px 10px", fontSize: 13 }}
        onClick={() => setNeedRefresh(false)}
      >
        Later
      </button>
    </div>
  );
}

/**
 * Tiny build-time stamp + "force refresh" link for the app footer / sidebar.
 * If the SW is stuck on an old bundle (which has happened: see Beshan's
 * 2026-06-08 cents/rupees confusion), the user can manually wipe caches and
 * reload.
 */
export function BuildBadge() {
  const ts = (() => {
    try {
      const d = new Date(__BUILD_TIME__);
      return d.toLocaleString("en-LK", {
        year: "numeric", month: "short", day: "2-digit",
        hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return "unknown";
    }
  })();

  async function forceRefresh() {
    if (!window.confirm("Force-reload ClearBooks? This clears all browser caches and reloads the latest version.")) return;
    try {
      // Unregister every SW under this scope.
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        for (const r of regs) await r.unregister();
      }
      // Wipe every Cache Storage entry.
      if ("caches" in window) {
        const keys = await caches.keys();
        for (const k of keys) await caches.delete(k);
      }
    } catch {
      /* best effort */
    }
    // Cache-bust the URL too so the browser doesn't use its memory cache.
    const u = new URL(window.location.href);
    u.searchParams.set("_t", String(Date.now()));
    window.location.replace(u.toString());
  }

  return (
    <button
      type="button"
      onClick={forceRefresh}
      title="Force-reload ClearBooks (clears every cache)"
      style={{
        background: "transparent",
        border: "none",
        color: "var(--ink-4)",
        cursor: "pointer",
        fontSize: 11,
        padding: 4,
        fontFamily: "inherit",
      }}
    >
      Build {ts} · ↻
    </button>
  );
}
